import {OrgRepo} from "../api/org.repo";
import {inject, Injectable} from "@angular/core";
import {Additionals} from "../item/additionals";
import {forkJoin, Observable, switchMap} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {OrgDto} from "../dto/org.dto";
import {DuckDbHelper} from "./duckdb.helper";
import {map} from "rxjs/operators";
import {isDefined} from "../../libs/utils/epic.utils";
import {PermissionScope} from "../../services/auth/permission-scope.enum";
import {PermissionGrant} from "../../services/auth/permission-grant.enum";
import {SettingDto} from "../dto/setting.dto";
import {CurrencyDto} from "../dto/currency.dto";
import {OrgFeatureDto} from "../dto/org-feature.dto";
import {Feature} from "../../libs/enum/feature.enum";
import {VatRateDuckDb} from "./vat-rate.duckdb";

/**
 * Bereiche des Viewers, die an einem Merkmal haengen: Merkmal, Recht und die Exporttabellen, deren Inhalt sie zeigen. Das
 * Bestattungsmodul fehlt mit Absicht - es war nie in Betrieb, und der Export enthaelt es nicht.
 */
const AREAS: readonly {feature: Feature, scope: PermissionScope, tables: readonly string[]}[] = [
  {feature: Feature.ARTICLE, scope: PermissionScope.ARTICLE, tables: ['article', 'goods_group']},
  {feature: Feature.CONTACT_VEHICLE, scope: PermissionScope.CONTACT_VEHICLE, tables: ['vehicle']},
  {feature: Feature.CONTACT_VEHICLE_EXTENDED, scope: PermissionScope.CONTACT_VEHICLE_EXTENDED, tables: ['vehicle_history', 'vehicle_repair', 'vehicle_tire']},
  {feature: Feature.CONTACT_RELATION, scope: PermissionScope.CONTACT_RELATION, tables: ['contact_relation']}
];

/**
 * Die Bereiche, zu denen der Export Daten enthaelt. Gebuchte Merkmale zaehlen im Viewer nicht: Die Daten gehoeren der
 * Organisation, auch wenn das Merkmal beim Export schon abgelaufen war - und ein Bereich ohne Daten braucht keine Seite.
 */
export function featuresWithData(tableSizes: ReadonlyMap<string, number>): Feature[] {
  return AREAS.filter((a) => a.tables.some((t) => (tableSizes.get(t) ?? 0) > 0)).map((a) => a.feature);
}

@Injectable({providedIn: 'root'})
export class OrgDuckDb extends OrgRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly vatRateDuckDb: VatRateDuckDb = inject(VatRateDuckDb);

  private settings: SettingDto[] = [];
  private org: OrgDto | undefined;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override readById(_id: string, _add?: Additionals): Observable<WrapperDto<OrgDto>> {
    if (isDefined(this.org)) {
      return DuckDbHelper.dataObs('OrgDto', this.org);
    }

    this.vatRateDuckDb.loadVats();
    return forkJoin([this.getFeatures(), this.getPermissions(), this.getSettings()])
      .pipe(switchMap(([features, permissions, settings]) => {
        return this.duck.sql('SELECT * FROM org').pipe(map((res) => {
          const data = res[0];
          data.enabled = true;
          data.taxNumber = data.tax_number;
          data.languageCode = data.language_code;
          data.countryCode = data.country_code;
          data.defaultCurrency = new CurrencyDto({code: data.default_currency});
          data.logoId = data.logo_id;
          data.permissions = permissions;
          data.permissionsExists = true;
          data.features = features;
          data.settings = settings;

          this.org = new OrgDto(data);
          this.settings = settings;
          return DuckDbHelper.data('OrgDto', this.org);
        }));
      }));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override updateSettings(_id: string, _settings: SettingDto[]): Observable<WrapperDto<SettingDto>> {
    return DuckDbHelper.datasObs('SettingDto', this.settings);
  }

  private getPermissions(): Observable<Map<PermissionScope, PermissionGrant>> {
    return this.getFeatures().pipe(map((features) => {
      const permissions: Map<PermissionScope, PermissionGrant> = new Map<PermissionScope, PermissionGrant>([
        [PermissionScope.INVOICE, PermissionGrant.R],
        [PermissionScope.INVOICE_PAYMENT, PermissionGrant.R],
        [PermissionScope.INVOICE_CANCEL, PermissionGrant.R],
        [PermissionScope.CONTACT, PermissionGrant.R],
        [PermissionScope.CONTACT_EMPLOYEE, PermissionGrant.R],
        [PermissionScope.RECEIPT, PermissionGrant.R],
        [PermissionScope.RECEIPT_PAYMENT, PermissionGrant.R],
        [PermissionScope.BILLING, PermissionGrant.R]
      ]);
      AREAS.filter((a) => features.some((f) => f.type === a.feature)).forEach((a) => permissions.set(a.scope, PermissionGrant.R));
      return permissions;
    }));
  }

  private getFeatures(): Observable<OrgFeatureDto[]> {
    // duckdb_tables() statt COUNT je Tabelle: Einem aelteren Export fehlt manche Tabelle, und die Zeilenzahl steht dort exakt.
    const select = `SELECT table_name AS name, estimated_size AS size FROM duckdb_tables() WHERE database_name = current_database()`;
    return this.duck.sql(select).pipe(map((res: any) => {
      const sizes: Map<string, number> = new Map<string, number>((DuckDbHelper.hasRes(res) ? res : []).map((row: any) => [row.name, Number(row.size)]));
      return featuresWithData(sizes).map((type) => new OrgFeatureDto({type: type, enabled: true, canceled: false}));
    }));
  }

  private getSettings(): Observable<SettingDto[]> {
    return this.duck.sql("SELECT key, value FROM setting WHERE KEY LIKE 'ORG_%'")
      .pipe(map((res) => res.map((row: any) => new SettingDto(row))));
  }
}
